// src/pages/Community/components/LikeBar.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  onSnapshot,
  runTransaction,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  getDoc
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { docAt, withBase } from "./fbPath";
import { db } from "../../../firebase";
import { toast } from "react-toastify";
import "./css/LikeBar.css";

// ✅ XP helpers (client-side enqueue; CF applies actual points)
import { enqueueLikeGive, enqueueLikeUndo } from "../../../services/xpService";
import { canonicalLikeMeta } from "../../../services/likeMeta";

// functions callable
import { getFunctions, httpsCallable } from "firebase/functions";

const DEBUG = false;
const log = (...args) => DEBUG && console.log("[LikeBar]", ...args);
const warn = (...args) => DEBUG && console.warn("[LikeBar]", ...args);

export default function LikeBar({
  basePath,
  className = "",
  xpDisabled = false, // set true if you ever want to turn off XP from outside
}) {
  const auth = getAuth();
  const user = auth.currentUser;
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  const { postCollection, likeModel } = canonicalLikeMeta(basePath?.[0]);

  // read parent like counter
  useEffect(() => {
    if (!basePath?.length) return;
    return onSnapshot(docAt(...basePath), (snap) => {
      setLikeCount(snap.data()?.likeCount ?? 0);
    });
  }, [JSON.stringify(basePath)]);

  // read my like
  useEffect(() => {
    if (!basePath?.length || !user) {
      setLiked(false);
      return;
    }
    return onSnapshot(docAt(...withBase(basePath, "likes", user.uid)), (s) => {
      setLiked(s.exists());
    });
  }, [JSON.stringify(basePath), user?.uid]);

  // helper: call server callable to update achievements counts
async function callUpdateAchievements(postId, deltaMap = {}, postOwnerUid = null) {
  if (!postId && !postOwnerUid) return null;
  try {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "updateAchievements");
    const payload = { postId: postId || "", deltaMap, source: "like", postOwnerUid };
    const res = await fn(payload);
    log("updateAchievements result", res?.data);
    return res?.data ?? null;
  } catch (err) {
    warn("updateAchievements callable failed", err);
    return null;
  }
}

// ... inside component
const toggleLike = useCallback(async () => {
  if (!user) {
    toast.info("Please login to like");
    return;
  }

  const likeRef = docAt(...withBase(basePath, "likes", user.uid));
  const parentRef = docAt(...basePath);

  // optimistic liked toggling (no numeric change)
  setLiked((prev) => !prev);

  try {
    let finalAction = null;
    let postOwnerUid = null;
    const postId = basePath?.[1] ?? null;

    await runTransaction(db, async (tx) => {
      const likeSnap = await tx.get(likeRef);
      const pSnap = await tx.get(parentRef);
      const current = pSnap.data()?.likeCount ?? 0;

      const pdata = pSnap.data() || {};
      postOwnerUid =
        pdata.ownerUid ||
        pdata.authorUid ||           // <-- added
        pdata.uid ||
        pdata.userId ||
        pdata.createdBy ||
        null;
      if (likeSnap.exists()) {
        // unlike
        tx.delete(likeRef);
        const updates = { likeCount: Math.max(0, current - 1) };
        if (basePath?.[0] === "community_posts") updates.likes = arrayRemove(user.uid);
        tx.update(parentRef, updates);
        finalAction = "unliked";
      } else {
        // like
        tx.set(likeRef, { createdAt: serverTimestamp() });
        const updates = { likeCount: current + 1 };
        if (basePath?.[0] === "community_posts") updates.likes = arrayUnion(user.uid);
        tx.update(parentRef, updates);
        finalAction = "liked";
      }
    });

    // After transaction, read authoritative parent likeCount and set it
    try {
      const snap = await getDoc(parentRef);
      setLikeCount(snap.data()?.likeCount ?? 0);
    } catch (e) {
      // ignore — onSnapshot will correct eventually
      console.warn("Failed to read parent likeCount after tx", e);
    }

    // update achievements via callable (best-effort)
    if (postId) {
      try {
        if (finalAction === "liked") await callUpdateAchievements(postId, { like: 1 }, postOwnerUid);
        else if (finalAction === "unliked") await callUpdateAchievements(postId, { like: -1 }, postOwnerUid);
      } catch (e) {
        console.warn("updateAchievements failed", e);
      }
    }

    // enqueue XP
    if (!xpDisabled && finalAction && user?.uid && basePath?.[1]) {
      try {
        if (finalAction === "liked") {
          await enqueueLikeGive({
            giverUid: user.uid,
            postId: basePath[1],
            postOwnerUid,
            postCollection: basePath[0],
            likeModel: basePath[0] === "community_posts" ? "array" : "subdoc",
            postCollection,
            likeModel,
          });
        } else {
          await enqueueLikeUndo({
            giverUid: user.uid,
            postId: basePath[1],
            postOwnerUid,
            postCollection: basePath[0],
            likeModel: basePath[0] === "community_posts" ? "array" : "subdoc",
            postCollection,
            likeModel,
          });
        }
      } catch {}
    }
  } catch (e) {
    // rollback optimistic liked if tx failed
    setLiked((prev) => !prev);
    toast.error("Failed to update like");
  }
}, [JSON.stringify(basePath), user?.uid, xpDisabled]);

  const loggedOut = !user;

  return (
    <div className={className}>
      <button
        type="button"
        className={`msn-action like-btn ${loggedOut ? "is-disabled" : ""}`}
        onClick={toggleLike}
        aria-pressed={liked}
        aria-disabled={loggedOut}
        title={loggedOut ? "Please login to like" : "Toggle like"}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !e.shiftKey) {
            e.preventDefault();
            toggleLike();
          }
        }}
      >
        {liked ? "💜 Liked" : "♡ Like"} · {likeCount}
      </button>
    </div>
  );
}
